/* Billing Terms → client agreement: signed (document uploaded on the merchant, read only here), start / expiry dates
   and validity. Dates need PTL_BILLING to change; every change is audited. */
import React, { useState } from 'react'
import { Alert, Box, Button, Card, CardContent, Chip, Dialog, DialogActions, DialogContent, DialogTitle, TextField, Typography } from '@mui/material'
import { LoadingButton } from '@mui/lab'
import { useSnackbar } from 'notistack'
import { useAgreementQuery, useRemoveAgreementMutation, useSaveAgreementMutation } from '../../../redux/ptlBillingApiSlice'
import { canBill, errText, fmtDate } from '../billingUtils'
import { creditDaysText } from '../../ptl-merchant-setup/components/MerchantStatus'

export const VALIDITY = {
  ACTIVE: { label: 'ACTIVE', color: 'success' },
  EXPIRING: { label: 'EXPIRING', color: 'warning' },
  EXPIRED: { label: 'EXPIRED', color: 'error' },
  NOT_STARTED: { label: 'NOT STARTED', color: 'default' },
  NONE: { label: 'NO DATES', color: 'default' },
}

export function AgreementChips({ signed, validity, daysToExpiry }) {
  const v = VALIDITY[validity] || VALIDITY.NONE
  return (
    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
      <Chip size='small' variant='outlined' color={signed ? 'success' : 'warning'} label={signed ? 'SIGNED' : 'UNSIGNED'} />
      <Chip size='small' color={v.color} label={validity === 'EXPIRING' && daysToExpiry != null ? `EXPIRES IN ${daysToExpiry} DAYS` : v.label} />
    </Box>
  )
}

export default function AgreementCard({ merchantOid }) {
  const { data, isFetching, error } = useAgreementQuery(merchantOid, { skip: !merchantOid })
  const [open, setOpen] = useState(false)
  const [remove, { isLoading: removing }] = useRemoveAgreementMutation()
  const { enqueueSnackbar } = useSnackbar()
  const billing = canBill()
  if (!merchantOid) return null
  const doRemove = async () => {
    if (!window.confirm('Remove the agreement dates of this client? The signed document stays.')) return
    const res = await remove(merchantOid)
    if (res.error) enqueueSnackbar(errText(res.error), { variant: 'error' })
    else enqueueSnackbar('Agreement dates removed', { variant: 'success' })
  }
  return (
    <Card elevation={0} sx={{ border: 1, borderColor: 'divider', mb: 2 }}>
      <CardContent sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
        <Box sx={{ flex: 1, minWidth: 260 }}>
          <Typography sx={{ fontWeight: 600 }}>Agreement {isFetching && '(loading…)'}</Typography>
          {error && <Alert severity='error' sx={{ mt: 1 }}>{errText(error)}</Alert>}
          {data && (
            <>
              <Box sx={{ my: 0.75 }}><AgreementChips signed={data.signed} validity={data.validity} daysToExpiry={data.daysToExpiry} /></Box>
              <Typography sx={{ fontSize: 13 }}>
                {data.startDate ? `${fmtDate(data.startDate)} to ${fmtDate(data.expiryDate)}` : 'No start / expiry dates yet.'}
                {creditDaysText(data.creditDays) ? ` · ${creditDaysText(data.creditDays)}` : ''}
                {data.remarks ? ` · ${data.remarks}` : ''}
              </Typography>
              <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
                Signed = the PTL agreement's signed document is uploaded (PTL Merchant Setup → Agreement). {data.updatedBy ? `Dates last changed by ${data.updatedBy}.` : ''}
              </Typography>
            </>
          )}
        </Box>
        {billing && <Button variant='outlined' onClick={() => setOpen(true)} sx={{ textTransform: 'none' }}>{data?.startDate ? 'Change dates' : 'Add dates'}</Button>}
        {billing && data?.startDate && <LoadingButton color='error' loading={removing} onClick={doRemove} sx={{ textTransform: 'none' }}>Remove dates</LoadingButton>}
      </CardContent>
      {open && <AgreementDialog merchantOid={merchantOid} current={data} onClose={() => setOpen(false)} />}
    </Card>
  )
}

function AgreementDialog({ merchantOid, current, onClose }) {
  const [v, setV] = useState({ startDate: current?.startDate || '', expiryDate: current?.expiryDate || '', remarks: current?.remarks || '' })
  const [save, { isLoading }] = useSaveAgreementMutation()
  const { enqueueSnackbar } = useSnackbar()
  const submit = async () => {
    const res = await save({ merchantOid, ...v, version: current?.version })
    if (res.error) return enqueueSnackbar(errText(res.error), { variant: 'error' })
    enqueueSnackbar('Agreement dates saved', { variant: 'success' })
    onClose()
  }
  return (
    <Dialog open onClose={onClose} maxWidth='xs' fullWidth>
      <DialogTitle>Agreement dates</DialogTitle>
      <DialogContent sx={{ display: 'grid', gap: 2, pt: '12px !important' }}>
        <TextField size='small' type='date' label='Start date' InputLabelProps={{ shrink: true }} value={v.startDate} onChange={(e) => setV({ ...v, startDate: e.target.value })} />
        <TextField size='small' type='date' label='Expiry date' InputLabelProps={{ shrink: true }} value={v.expiryDate} onChange={(e) => setV({ ...v, expiryDate: e.target.value })} />
        <TextField size='small' label='Remarks' value={v.remarks} onChange={(e) => setV({ ...v, remarks: e.target.value })} inputProps={{ maxLength: 300 }} />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <LoadingButton variant='contained' loading={isLoading} disabled={!v.startDate || !v.expiryDate} onClick={submit}>Save</LoadingButton>
      </DialogActions>
    </Dialog>
  )
}
